import type { FC } from "react";
import {
  Box,
  Button,
  Typography,
  makeStyles,
  Grid,
  Card,
  CardContent,
  Hidden,
  useTheme,
  useMediaQuery,
  Theme
} from "@material-ui/core";
import { QuoteLineItem } from "../../../api/Quote/QuoteLineItem";
import React from "react";
import { QuoteHr } from "../../quotePage/components/QuoteHr";
import { colors } from "swyft-react-core";
import QuoteAddonChangeQuantity from "../../quotePage/components/QuoteAddonChangeQuantity";
import { hasAddons, quateAddonQuantityValue } from "../../../app/utils";
import QuoteButtonAdd from "../../quotePage/components/QuoteButtonAdd";
import { PriceFormat } from "../../../components/typography/PriceFormat";

interface QuoteAddonWizardAddonsProps {
  className?: string;
  addons?: QuoteLineItem[];
  onAddAddon: (addon: QuoteLineItem) => void;
  onInfoDialogOpen: (addon: QuoteLineItem) => void;
  onChangeQuantity: (addon: QuoteLineItem, quantity: number) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '& [class*="Button-contained.Mui-disabled"]': {
      backgroundColor: colors.white,
      border: "1px dashed black",
      fontWeight: "bold",
      color: colors.darkGrey
    }
  },
  box: {
    boxShadow: "0px 3px 6px 0px rgba(0, 0, 0, 0.16)"
  },
  image: {
    margin: "0 auto",
    display: "block",
    width: "100%",
    maxWidth: "15rem"
  },
  icon: {
    verticalAlign: "text-top"
  }
}));

const QuoteAddonWizardAddons: FC<QuoteAddonWizardAddonsProps> = ({
  addons,
  onAddAddon,
  onInfoDialogOpen,
  onChangeQuantity
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  if (!hasAddons(addons)) {
    return null;
  }

  const addonView = function (addon: QuoteLineItem) {
    if (!addon) {
      return null;
    }

    const onView = function () {
      onInfoDialogOpen(addon);
    };

    return (
      <Box mb={isSmall ? 1 : 3} key={addon.name + "wizard-addon"} className={classes.box}>
        <Card square>
          <CardContent>
            <Grid container alignItems="center">
              <Grid item md={4} xs={12}>
                <Box>
                  <img className={classes.image} alt={addon.name} src={addon.imageUrl} />
                </Box>
              </Grid>
              <Grid item md={8} xs={12}>
                <Box textAlign="center">
                  <Typography variant="h2">{addon.name}</Typography>
                  <Typography variant="body1">{addon.highLevelDescription}</Typography>
                  <Box mt={isSmall ? 1 : 3}>
                    <QuoteAddonChangeQuantity
                      addon={addon}
                      onChangeQuantity={onChangeQuantity}
                      hideOnSmall={false}
                      hideOnMin={true}
                      showRecommend={true}
                      showOneLine={false}
                      disabled={false}
                    />
                  </Box>
                  <Box mt={isSmall ? 1 : 3}>
                    <Typography variant={isSmall ? "h2" : "h6"}>
                      <PriceFormat value={quateAddonQuantityValue(addon)} />
                    </Typography>
                  </Box>
                  <Hidden lgUp>
                    <Box mt={3} mb={3} mx="auto" className={classes.root} width="40%">
                      <QuoteButtonAdd addon={addon} onAddAddon={onAddAddon} fullWidth />
                    </Box>
                  </Hidden>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <QuoteHr />
                <Box mt={isSmall ? 1 : 3} display="flex" alignItems="center">
                  <Box flexGrow={1} textAlign={{ xs: "center", md: "left" }}>
                    <Button onClick={onView} color="secondary">
                      View Details
                    </Button>
                  </Box>
                  <Hidden mdDown>
                    <Box className={classes.root} width="20%">
                      <QuoteButtonAdd addon={addon} onAddAddon={onAddAddon} fullWidth />
                    </Box>
                  </Hidden>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  };

  return <React.Fragment>{addons!.map((li) => addonView(li))}</React.Fragment>;
};

export default QuoteAddonWizardAddons;
